import React from "react";
import { motion } from "framer-motion";
import { MapPin, Sparkles, Trophy } from "lucide-react";

const REAL_SCREENS = {
    feed: "https://customer-assets-rejwkqb3.emergentagent.net/job_watchpoint-hub-1/artifacts/zhe6vx2f_IMG_1098.webp",
    capture:
        "https://customer-assets-rejwkqb3.emergentagent.net/job_watchpoint-hub-1/artifacts/8qr4429q_IMG_1097.webp",
};

/** Photorealistic phone frame wrapping a real product screenshot */
export default function PhoneMockup({ variant = 1 }) {
    return (
        <div className="relative aspect-[9/19] w-full max-w-[280px] mx-auto">
            <div className="absolute -inset-10 rounded-[3rem] bg-[radial-gradient(ellipse_at_center,rgba(30,107,255,0.25),transparent_65%)] blur-2xl" />
            <div className="relative w-full h-full rounded-[2.4rem] bg-black border border-white/10 phone-shadow overflow-hidden">
                {/* notch */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 bg-black rounded-full z-30" />
                {/* screen */}
                <div className="absolute inset-2 rounded-[2rem] overflow-hidden bg-black">
                    {variant === 1 && <RealCapture />}
                    {variant === 2 && <AiAnalysis />}
                    {variant === 3 && <RealFeed />}
                </div>
                {/* corner label */}
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 font-mono-e text-[9px] tracking-[0.28em] text-sn-fog uppercase whitespace-nowrap">
                    {variant === 1 && "01 · Segnala"}
                    {variant === 2 && "02 · IA · Stima"}
                    {variant === 3 && "03 · Feed civico"}
                </div>
            </div>
        </div>
    );
}

const RealCapture = () => (
    <img
        src={REAL_SCREENS.capture}
        alt="Sentinel · Nuova segnalazione"
        className="w-full h-full object-cover"
        loading="lazy"
    />
);

const RealFeed = () => (
    <img
        src={REAL_SCREENS.feed}
        alt="Sentinel · Feed civico"
        className="w-full h-full object-cover"
        loading="lazy"
    />
);

/** Synthetic IA analysis screen (middle step) */
const AiAnalysis = () => (
    <div className="w-full h-full flex flex-col p-4 pt-8 text-sn-silver bg-gradient-to-b from-[#0B0B12] to-[#141420]">
        <div className="flex items-center justify-between font-mono-e text-[8px] tracking-[0.2em] text-sn-fog uppercase">
            <span>12:52</span>
            <span className="text-sn-blue">◉ AI</span>
        </div>
        <div className="mt-3 font-mono-e text-[9px] tracking-[0.2em] text-sn-blue uppercase">
            Analisi AI · in corso
        </div>
        <h4 className="font-serif text-2xl leading-tight mt-1 text-white">
            Norma & danni
        </h4>

        <div className="mt-4 rounded-lg border border-sn-blue/40 bg-sn-blue/10 p-3">
            <div className="flex items-center gap-2 font-mono-e text-[8px] uppercase tracking-widest text-sn-fog">
                <Sparkles size={10} className="text-sn-blue" /> Match norma
            </div>
            <div className="mt-1 text-[11px] text-white">
                Art. 674 c.p. — Getto pericoloso di cose
            </div>
            <div className="mt-2 h-1 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: "92%" }}
                    transition={{ duration: 1.4 }}
                    className="h-full bg-sn-blue"
                />
            </div>
        </div>

        <div className="mt-3 rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="font-mono-e text-[8px] uppercase tracking-widest text-sn-fog">
                Stima danni
            </div>
            <div className="font-serif text-3xl mt-1 text-white">
                € 601
            </div>
            <div className="font-mono-e text-[8px] uppercase tracking-widest text-sn-blue mt-1">
                Priorità · ALTA
            </div>
        </div>

        <div className="mt-3 flex items-center gap-2 font-mono-e text-[8px] uppercase tracking-widest text-sn-fog">
            <MapPin size={10} className="text-sn-blue" /> Corralejo · Spagna
        </div>

        <div className="mt-auto grid grid-cols-3 gap-1.5">
            {[
                { l: "Traffico", on: false },
                { l: "Ambiente", on: false },
                { l: "Decoro", on: true },
            ].map((t) => (
                <div
                    key={t.l}
                    className={`text-center rounded-md py-1.5 text-[8px] uppercase tracking-widest font-mono-e ${t.on ? "bg-sn-blue text-white" : "bg-white/5 text-sn-fog border border-white/10"}`}
                >
                    {t.l}
                </div>
            ))}
        </div>

        <div className="mt-3 flex items-center gap-1 font-mono-e text-[8px] uppercase tracking-widest text-sn-fog">
            <Trophy size={10} className="text-sn-blue" />
            + 12 punti · Guardiano Lv4
        </div>
    </div>
);
